import { useRef, useState } from 'react';
import { Button, Checkbox, Dialog, InlineError, Input, Select } from '../../components/ui';
import { IconTile } from '../../components/ui/Icon';
import { useTapPeople } from '../projects/hooks';
import {
  presetDueDate,
  TASK_TYPE_LABELS,
  type ReschedulePreset,
  type SalesTask,
} from './salesTasksLens';
import {
  salesTaskErrorMessage,
  useRecycleSalesTask,
  useSalesTaskAssociations,
  useSetSalesTaskDeal,
  useSetSalesTaskCompleted,
  useUpdateSalesTask,
  type SalesTaskChanges,
} from './queries';
import type { Database } from '../../types/database.types';
import './salesTasks.css';

type TaskType = Database['public']['Enums']['task_type'];

/**
 * One sales task, opened.
 *
 * ── Why this is not TaskDetailsPanel ───────────────────────────────────────
 *
 * The Hub already has a full task panel, and it cannot be used here. It takes
 * `projectId` and `projectName` as required props, and everything inside it —
 * subtasks, files, comments, supplier reviews, the field picker — is scoped to
 * a project. A sales task has `deal_id` set and `project_id` NULL; that is the
 * `tasks_one_parent` constraint, not an oversight. My Day gets away with the
 * panel because it has a real personal project to point at. We would have to
 * invent one, and an invented project id is the kind of thing that works until
 * somebody clicks Files.
 *
 * So this is a small purpose-made dialog over the task fields and the records
 * it is associated with.
 *
 * ── Why the row has two click targets ──────────────────────────────────────
 *
 * Kyu asked for a "half-screen dialog opening the record profile" AND for
 * "Task Rescheduling & Options". Those pull apart: the first opens a company,
 * the second needs a dialog about the task. HubSpot, which he asked us to
 * follow, resolves it by splitting the row — the task title opens the task,
 * the record name beside it opens the record. Kyu, 15 September, choosing
 * between three options: "Both, split by column."
 *
 * ── What is not here yet ───────────────────────────────────────────────────
 *
 * Due TIME, reminders and repeat still need their own design note first: a
 * time column means deciding what a task with no time means, and that is a
 * data question.
 *
 * Opening the record still navigates to its own screen rather than stacking a
 * second drawer over this one. Mounting DealDrawer here needs the deal's
 * pipeline plus three pipeline-scoped lists (sections, stages, people), which
 * is a real cost and its own step.
 */

/** Blank means unassigned, and has to round-trip to NULL rather than to ''. */
const UNASSIGNED = '';
const UNLINKED = '';

const TYPE_OPTIONS = (Object.keys(TASK_TYPE_LABELS) as (keyof typeof TASK_TYPE_LABELS)[]).map(
  (key) => ({ value: key, label: TASK_TYPE_LABELS[key] }),
);

export function SalesTaskDialog({ task, onClose }: { task: SalesTask; onClose: () => void }) {
  const update = useUpdateSalesTask();
  const setCompleted = useSetSalesTaskCompleted();
  const setDeal = useSetSalesTaskDeal();
  const recycle = useRecycleSalesTask();
  // Only when the dialog is open. The list behind it does not need the sales
  // team, and forty rows should not pay for a lookup nobody asked for.
  const people = useTapPeople(true);
  const associations = useSalesTaskAssociations(task.deal_id);
  const [error, setError] = useState('');
  // Held locally so typing does not write a row per keystroke. Everything else
  // is a single deliberate choice and saves as it is made.
  const [title, setTitle] = useState(task.title);
  const [dueDate, setDueDate] = useState(task.due_date ?? '');
  const [recycling, setRecycling] = useState(false);
  const [recycleTitle, setRecycleTitle] = useState(task.title);
  const [recycleDueDate, setRecycleDueDate] = useState(presetDueDate(new Date(), 'tomorrow'));
  const dueDateInput = useRef<HTMLInputElement>(null);
  // The last due date written, so blur and close never write the same date twice.
  const savedDueDate = useRef(task.due_date);

  function save(changes: SalesTaskChanges) {
    setError('');
    update.mutate(
      { taskId: task.id, changes },
      { onError: (cause: unknown) => setError(salesTaskErrorMessage(cause)) },
    );
  }

  function saveTitle() {
    const next = title.trim();
    // An empty title would leave a row nobody can identify in a list whose
    // whole job is identifying rows. Refuse and put the old one back.
    if (next === '') {
      setTitle(task.title);
      return;
    }
    if (next === task.title) {
      return;
    }
    save({ title: next });
  }

  function saveDueDate() {
    const next = dueDate === '' ? null : dueDate;
    if (next !== savedDueDate.current) {
      savedDueDate.current = next;
      save({ due_date: next });
    }
  }

  // Escape and a click on the backdrop unmount the dialog without a blur, so
  // a date still sitting in the field would be thrown away (Nez, 5 Oct).
  function close() {
    saveDueDate();
    onClose();
  }

  function choosePreset(preset: ReschedulePreset) {
    setDueDate(presetDueDate(new Date(), preset));
    // A preset fills the existing field; it does not save by itself. Keeping
    // focus here means the ordinary blur path remains the one write path.
    dueDateInput.current?.focus();
  }

  function pickDate() {
    dueDateInput.current?.focus();
    dueDateInput.current?.showPicker?.();
  }

  const assigneeOptions = [
    { value: UNASSIGNED, label: 'Unassigned' },
    ...(people.data ?? []).map((person) => ({
      value: person.id,
      label: person.display_name === '' ? person.email : person.display_name,
    })),
  ];
  const leadOptions = [
    { value: UNLINKED, label: 'No lead linked' },
    ...(associations.data?.leads ?? []).map((lead) => ({ value: lead.id, label: lead.label })),
  ];
  const companyOptions = [
    { value: UNLINKED, label: 'No company linked' },
    ...(associations.data?.companies ?? []).map((company) => ({
      value: company.id,
      label: company.label,
    })),
  ];
  const dealOptions = [
    { value: UNLINKED, label: 'No deal linked' },
    ...(associations.data?.deals ?? []).map((deal) => ({ value: deal.id, label: deal.name })),
  ];
  const linkedDeal = associations.data?.linkedDeal ?? null;
  const carriedOver = [
    task.deal_id === null
      ? null
      : { label: 'Deal', value: linkedDeal?.name ?? task.record_name ?? 'Deal' },
    task.lead_id === null
      ? null
      : {
          label: 'Lead',
          value: associations.data?.leads.find((lead) => lead.id === task.lead_id)?.label ?? 'Lead',
        },
    task.company_id === null
      ? null
      : {
          label: 'Company',
          value:
            associations.data?.companies.find((company) => company.id === task.company_id)?.label ??
            'Company',
        },
    task.prospect_id === null
      ? null
      : {
          label: 'Prospect',
          value:
            associations.data?.prospects.find((prospect) => prospect.id === task.prospect_id)
              ?.label ?? 'Prospect',
        },
  ].filter((entry): entry is { label: string; value: string } => entry !== null);

  if (recycling) {
    return (
      <Dialog
        open
        title="Recycle task"
        onClose={() => setRecycling(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRecycling(false)}>
              Cancel
            </Button>
            <Button
              disabled={recycle.isPending}
              onClick={() => {
                const nextTitle = recycleTitle.trim();
                if (nextTitle === '' || recycleDueDate === '') {
                  setError('A recycled task needs a title and due date.');
                  return;
                }
                setError('');
                recycle.mutate(
                  { taskId: task.id, title: nextTitle, dueDate: recycleDueDate },
                  {
                    onSuccess: onClose,
                    onError: (cause: unknown) => setError(salesTaskErrorMessage(cause)),
                  },
                );
              }}
            >
              {recycle.isPending ? 'Recycling…' : 'Recycle task'}
            </Button>
          </>
        }
      >
        {error === '' ? null : <InlineError>{error}</InlineError>}
        <div className="sales-task-dialog">
          <Input
            label="Task"
            value={recycleTitle}
            onChange={(event) => setRecycleTitle(event.target.value)}
          />
          <Input
            label="Due (GMT+8)"
            type="date"
            value={recycleDueDate}
            onChange={(event) => setRecycleDueDate(event.target.value)}
          />

          <section className="sales-task-carried" aria-labelledby="sales-task-carried-title">
            <h3 id="sales-task-carried-title" className="sales-task-associations-title">
              Carried over
            </h3>
            <dl className="sales-task-carried-list">
              {carriedOver.map((entry) => (
                <div key={entry.label} className="sales-task-carried-row">
                  <dt>{entry.label}</dt>
                  <dd>{entry.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </Dialog>
    );
  }

  return (
    /* A Dialog, not a Drawer.
       This opened in the `wide` drawer, which is 78vw because it was sized for
       the three-column record profile. A task holds a tick, three fields and
       the name of its record, so the panel arrived nearly the width of the
       screen and full height with most of it empty. Hanneza, 15 September:
       "The dialog box opens extremely large with a massive block of empty
       space below the fields." A dialog sizes to its content, which is the
       shape this actually is. */
    <Dialog
      open
      title={task.title}
      onClose={close}
      footer={
        <div className="sales-task-dialog-actions">
          <Button
            variant="ghost"
            disabled={task.is_completed}
            onClick={() => {
              setError('');
              setRecycleTitle(task.title);
              setRecycleDueDate(presetDueDate(new Date(), 'tomorrow'));
              setRecycling(true);
            }}
          >
            Recycle
          </Button>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
        </div>
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <div className="sales-task-dialog">
        {/* First, because it is the one thing you can do to a task without
            reading any of the rest of it. */}
        <Checkbox
          label={task.is_completed ? 'Completed' : 'Mark complete'}
          checked={task.is_completed}
          disabled={setCompleted.isPending}
          onChange={() => {
            setError('');
            setCompleted.mutate(
              { taskId: task.id, isCompleted: !task.is_completed },
              { onError: (cause: unknown) => setError(salesTaskErrorMessage(cause)) },
            );
          }}
        />

        <Input
          label="Task"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={saveTitle}
          // Enter saves, because a one-line field that only saves on blur
          // makes people click elsewhere to find out whether it worked.
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
        />

        <div className="sales-task-dialog-row">
          <Select
            label="Type"
            value={task.task_type ?? 'todo'}
            options={TYPE_OPTIONS}
            // Cast to the enum itself, not to SalesTaskChanges['task_type']:
            // under exactOptionalPropertyTypes that lookup includes undefined,
            // and writing undefined into the object is a different statement
            // from leaving the key out.
            onChange={(event) => save({ task_type: event.target.value as TaskType })}
          />

          <Select
            label="Assigned to"
            value={task.assignee_id ?? UNASSIGNED}
            options={assigneeOptions}
            disabled={people.isPending}
            onChange={(event) =>
              save({ assignee_id: event.target.value === UNASSIGNED ? null : event.target.value })
            }
          />

          <Input
            ref={dueDateInput}
            label="Due (GMT+8)"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
            onBlur={saveDueDate}
          />
        </div>

        <div className="sales-task-reschedule" aria-label="Quick reschedule">
          <span className="sales-task-reschedule-label">Reschedule</span>
          <Button size="sm" variant="secondary" onClick={() => choosePreset('tomorrow')}>
            Tomorrow
          </Button>
          <Button size="sm" variant="secondary" onClick={() => choosePreset('week')}>
            +1 week
          </Button>
          <Button size="sm" variant="secondary" onClick={() => choosePreset('month')}>
            +1 month
          </Button>
          <Button size="sm" variant="ghost" onClick={pickDate}>
            Pick a date
          </Button>
        </div>

        <section className="sales-task-associations" aria-labelledby="sales-task-associations">
          <h3 id="sales-task-associations" className="sales-task-associations-title">
            Associated records
          </h3>

          {associations.isError ? (
            <InlineError>{salesTaskErrorMessage(associations.error)}</InlineError>
          ) : null}

          <div className="sales-task-dialog-row sales-task-associations-grid">
            <div className="sales-task-association-field">
              <div className="sales-task-association-label">
                <IconTile name="deals" tone="purple" />
                <span>Deal</span>
              </div>
              <Select
                label="Deal"
                labelHidden
                value={task.deal_id ?? UNLINKED}
                options={dealOptions}
                disabled={associations.isPending || setDeal.isPending}
                onChange={(event) => {
                  setError('');
                  setDeal.mutate(
                    {
                      taskId: task.id,
                      dealId: event.target.value === UNLINKED ? null : event.target.value,
                    },
                    { onError: (cause: unknown) => setError(salesTaskErrorMessage(cause)) },
                  );
                }}
              />
            </div>

            <div className="sales-task-association-field">
              <div className="sales-task-association-label">
                <IconTile name="nurture" tone="green" />
                <span>Lead</span>
              </div>
              <Select
                label="Lead"
                labelHidden
                value={task.lead_id ?? UNLINKED}
                options={leadOptions}
                disabled={associations.isPending}
                onChange={(event) =>
                  save({ lead_id: event.target.value === UNLINKED ? null : event.target.value })
                }
              />
            </div>

            <div className="sales-task-association-field">
              <div className="sales-task-association-label">
                <IconTile name="workspace" tone="blue" />
                <span>Company</span>
              </div>
              <Select
                label="Company"
                labelHidden
                value={task.company_id ?? UNLINKED}
                options={companyOptions}
                disabled={associations.isPending}
                onChange={(event) =>
                  save({ company_id: event.target.value === UNLINKED ? null : event.target.value })
                }
              />
            </div>
          </div>

          {task.deal_id === null ? null : (
            <div className="sales-task-hubspot">
              <span className="sales-task-hubspot-label">HubSpot</span>
              {linkedDeal?.hubspotUrl ? (
                <a
                  className="sales-task-dialog-record-link"
                  href={linkedDeal.hubspotUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open deal in HubSpot
                </a>
              ) : (
                <span className="sales-task-association-empty">No HubSpot link</span>
              )}
              <span className="ui-badge">from the deal</span>
            </div>
          )}
        </section>
      </div>
    </Dialog>
  );
}
